import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", city: "", intention: "", candle: false, anonymous: false };

function Flame() {
  return (
    <svg viewBox="0 0 24 40" className="h-10 w-6 animate-flame" aria-hidden="true">
      <path d="M12 2 C16 10 20 14 20 22 A8 8 0 0 1 4 22 C4 14 8 10 12 2 Z" fill="#D4AF37" />
      <path d="M12 15 C14 18.5 16 20.5 16 24 A4 4 0 0 1 8 24 C8 20.5 10 18.5 12 15 Z" fill="#FBF9F4" opacity="0.85" />
    </svg>
  );
}

export default function PrayerModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/prayer-requests", form);
      toast.success("Pedido enviado ao altar. Nossos sacerdotes orarão por você.");
      setForm(EMPTY);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível enviar o pedido."));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-[#FBF9F4]/5 border border-[#C5A059]/25 px-4 py-3 text-sm text-[#FBF9F4] placeholder-[#FBF9F4]/35 focus:outline-none focus:border-[#C5A059] transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center bg-[#140709]/92 backdrop-blur-md p-4"
          onClick={onClose}
        >
          <motion.div
            data-testid="prayer-request-modal"
            initial={{ opacity: 0, y: 48, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 48, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-[#140709] border border-[#C5A059]/30 p-8 sm:p-10 text-[#FBF9F4]"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="prayer-modal-close" onClick={onClose} aria-label="Fechar" className="absolute top-4 right-4 p-2 text-[#FBF9F4]/60 hover:text-[#C5A059] transition-colors">
              <X className="h-5 w-5" />
            </button>

            <p className="overline-label text-[#C5A059]">Pedido de Oração</p>
            <h3 className="font-display mt-3 text-2xl sm:text-3xl font-bold">Apresente suas intenções ao altar do Senhor</h3>
            <p className="font-liturgy mt-3 text-lg italic text-[#FBF9F4]/65">Nossos sacerdotes orarão por você nas missas da diocese.</p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <input
                data-testid="prayer-form-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome completo"
                className={inputCls}
              />
              <input
                data-testid="prayer-form-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Cidade / Estado"
                className={inputCls}
              />
              <textarea
                data-testid="prayer-form-intention"
                required
                rows={4}
                value={form.intention}
                onChange={(e) => setForm({ ...form, intention: e.target.value })}
                placeholder="Sua intenção de oração (saúde, família, trabalho, falecidos, agradecimento...)"
                className={`${inputCls} resize-none`}
              />

              <div className="flex flex-wrap gap-6 pt-2">
                <label className="flex cursor-pointer items-center gap-3 text-sm text-[#FBF9F4]/75">
                  <input
                    data-testid="prayer-form-candle"
                    type="checkbox"
                    checked={form.candle}
                    onChange={(e) => setForm({ ...form, candle: e.target.checked })}
                    className="h-4 w-4 accent-[#C5A059]"
                  />
                  Acender vela virtual
                </label>
                <label className="flex cursor-pointer items-center gap-3 text-sm text-[#FBF9F4]/75">
                  <input
                    data-testid="prayer-form-anonymous"
                    type="checkbox"
                    checked={form.anonymous}
                    onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                    className="h-4 w-4 accent-[#C5A059]"
                  />
                  Manter anônimo
                </label>
              </div>

              <AnimatePresence>
                {form.candle && (
                  <motion.div
                    data-testid="prayer-candle-flame"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-4 overflow-hidden border border-[#C5A059]/20 bg-[#C5A059]/5 px-5 py-3"
                  >
                    <Flame />
                    <p className="font-liturgy text-base italic text-[#FBF9F4]/75">Uma vela será acesa por sua intenção na Sé Primacial.</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                data-testid="prayer-request-modal-submit"
                type="submit"
                disabled={sending}
                className="overline-label w-full bg-[#C5A059] py-4 text-[#140709] hover:bg-[#D4AF37] disabled:opacity-50 transition-colors"
              >
                {sending ? "Enviando..." : "Enviar Pedido para o Altar"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
